import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {AbstractContactModel, ContactType} from '@app/models/abstract-contact.model';
import {PhoneNumberFormatter} from '@app/shared/formatting.pipes';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtNameValueService} from '@app/services/gt-name-value.service';
import {GtServiceNumbersUtil} from '@app/shared/utils/gt-service-numbers.util';
import {environment} from '@environment';
import {EndUserAccess} from '@gt-api';
import {of, Subject} from 'rxjs';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {forwardingTargetNameValues, forwardingTargets, forwardingTypes} from '@app/models/status.constants';
import {FWD_SETTINGS_CATEGORY} from '../user-settings/user-settings.component';
import {StatusAvailableModel} from '@app/models/status-available.model';
import {catchError, map, switchMap, tap} from 'rxjs/operators';

@Component({
  selector: 'gt-forwarding-settings-status-available',
  templateUrl: './forwarding-settings.component.html',
  styleUrls: ['./forwarding-settings.component.scss']
})
export class ForwardingSettingsComponent implements OnInit, OnDestroy {
  forwardingTypes = forwardingTypes;
  forwardingTargets = forwardingTargets;

  forwardingDelays = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

  colleagueNumber: string;
  mainNumber: string;
  otherNumber: string;
  forwardingDelay: number;
  forwardingType;
  forwardingTarget = 1;
  mainNumbers: AbstractContactModel[];
  colleagueNumbers: AbstractContactModel[];
  settingsCategoryText: string;
  hasPrivateNumberSetting = false;
  hasWorkinghoursSetting = environment.hasOutsideOfWorkingHours;

  private nameValueExtension = '';
  private noReplyTimeName: string;
  private activeProfileName: string;

  @Input() settingsCategory: FWD_SETTINGS_CATEGORY = FWD_SETTINGS_CATEGORY.BUSINESS;

  @Input() status: StatusAvailableModel;

  @Output() backObservable = new EventEmitter<void>();
  @Input() login: string;
  @Input() orgname: string;
  @Input() canEdit = true;

  private destroyed = new Subject<void>();

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private notificationService: GtNotificationService,
    private nameValueService: GtNameValueService,
    private phoneNumberFormatter: PhoneNumberFormatter
  ) {}

  ngOnInit() {
    this.login = this.login ? this.login : this.authenticationService.login;
    this.orgname = this.orgname ? this.orgname : this.authenticationService.oname;

    switch (this.settingsCategory) {
      case FWD_SETTINGS_CATEGORY.BUSINESS:
        this.settingsCategoryText = 'forwarding.business_type';
        this.initForwardingSettings();
        break;
      case FWD_SETTINGS_CATEGORY.OUT_OF_OFFICE:
        this.settingsCategoryText = 'forwarding.out_of_office_type';
        this.nameValueExtension = 'OOO';
        if (this.hasWorkinghoursSetting) {
          this.initForwardingSettings();
        }
        break;
      case FWD_SETTINGS_CATEGORY.PRIVATE:
        this.settingsCategoryText = 'forwarding.private_type';
        this.nameValueExtension = 'Private';
        if (environment.hasPrivateNumber) {
          this.directoryService.getEndUserAccess(this.orgname, this.login).subscribe((endUserAccessList: EndUserAccess[]) => {
            this.hasPrivateNumberSetting = endUserAccessList.some((endUserAccess: EndUserAccess) => endUserAccess.category === 0);
            if (this.hasPrivateNumberSetting) {
              this.initForwardingSettings();
            }
          });
        }
        break;
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  canShowSettings() {
    return (
      this.settingsCategory === FWD_SETTINGS_CATEGORY.BUSINESS ||
      (this.settingsCategory === FWD_SETTINGS_CATEGORY.OUT_OF_OFFICE && this.hasWorkinghoursSetting) ||
      (this.settingsCategory === FWD_SETTINGS_CATEGORY.PRIVATE && this.hasPrivateNumberSetting)
    );
  }

  private initForwardingSettings() {
    this.directoryService.getServiceNumberModels(this.orgname, false).subscribe(
      snm => {
        this.mainNumbers = snm
          .filter(n => GtServiceNumbersUtil.isMainNumber(n))
          .sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return 0;
          });
      },
      err => this.error(err)
    );

    this.directoryService.getContacts(this.orgname, false).subscribe(
      contacts => {
        this.colleagueNumbers = contacts
          .filter(c => c.type === ContactType.EndUser && c.login !== this.login)
          .sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return 0;
          });
      },
      err => this.error(err)
    );

    this.noReplyTimeName = 'NoReplyTimeFN' + this.nameValueExtension;
    this.activeProfileName = 'ActiveProfile' + this.nameValueExtension;

    this.nameValueService.getEndUserNameValue(this.activeProfileName, this.orgname, this.login).subscribe(profile => {
      const profileNumber = Number(profile);
      if (profileNumber <= 4) {
        // forwarding after
        this.forwardingType = 0;
        this.forwardingTarget = profileNumber;
      } else if (profileNumber <= 8) {
        // forwarding immediately
        this.forwardingType = 4;
        this.forwardingTarget = profileNumber - 4;
      } else {
        // no forwarding
        this.forwardingType = 9;
      }
    });

    this.nameValueService.getEndUserNameValue(this.noReplyTimeName, this.orgname, this.login).subscribe(nr => {
      this.forwardingDelay = Number(nr);
    });

    forwardingTargetNameValues.forEach(s => {
      this.nameValueService.getEndUserNameValue(s.name + this.nameValueExtension, this.orgname, this.login).subscribe(
        r => {
          switch (s.forwardingTarget) {
            case 2:
              this.colleagueNumber = r;
              break;
            case 3:
              this.mainNumber = r;
              break;
            case 4:
              this.otherNumber = this.phoneNumberFormatter.transform(r);
              break;
          }
        },
        err => this.error(err)
      );
    });
  }

  private error = error => {
    this.notificationService.errorNotification.next(error);
  };

  updateForwardingDelay() {
    this.nameValueService.setEndUserNameValue(this.noReplyTimeName, this.forwardingDelay + '', this.orgname, this.login).subscribe(
      () => {
        if (this.settingsCategory === FWD_SETTINGS_CATEGORY.BUSINESS) {
          this.status.forwardingDelay = this.forwardingDelay;
        }
      },
      err => this.error(err)
    );
  }

  updateForwardingForStatusAvailable() {
    const value = this.forwardingType + this.forwardingTarget;

    switch (this.forwardingType) {
      case 9:
        this.nameValueService.setEndUserNameValue(this.activeProfileName, '9', this.orgname, this.login).subscribe(
          () => {
            this.forwardingTarget = 1;
            this.forwardingDelay = 0;
            this.updateCurrentStatus();
          },
          err => this.error(err)
        );
        break;
      case 4:
        this.nameValueService.setEndUserNameValue(this.activeProfileName, value + '', this.orgname, this.login).subscribe(
          () => {
            this.forwardingDelay = 0;
            this.updateCurrentStatus();
          },
          err => this.error(err)
        );
        break;
      case 0:
        this.nameValueService.setEndUserNameValue(this.activeProfileName, value + '', this.orgname, this.login).subscribe(
          () => {
            this.updateCurrentStatus();
          },
          err => this.error(err)
        );
        break;
    }
  }

  private updateCurrentStatus() {
    if (this.settingsCategory === FWD_SETTINGS_CATEGORY.BUSINESS) {
      this.status.forwardingDelay = this.forwardingDelay;
      this.status.forwardingType = this.forwardingType;
      this.status.forwardingTarget = this.forwardingTarget;

      switch (this.status.forwardingTarget) {
        case 2:
          this.status.forwardingNumber = this.colleagueNumber;
          break;
        case 3:
          this.status.forwardingNumber = this.mainNumber;
          break;
        case 4:
          this.status.forwardingNumber = this.otherNumber;
          break;
        default:
          this.status.forwardingNumber = '';
          break;
      }
    }
  }

  onBack() {
    if (this.forwardingType !== 0) {
      this.forwardingDelay = 0;
    }
    this.updateCurrentStatus();
    this.backObservable.emit();
  }

  changeNumber(event?) {
    let value;
    if (event) {
      value = event.value;
    } else {
      value = GtPhoneNumberUtil.toBackend(this.otherNumber);
    }
    const nameValueKey = forwardingTargetNameValues.find(e => e.forwardingTarget === this.status.forwardingTarget).name;
    this.directoryService
      .getContacts(this.orgname, false)
      .pipe(
        map(contacts => contacts.find(c => c.phoneNumber === value)),
        tap(contact => {
          if (contact?.login === this.login) {
            throw Error('forwarding.error_forward_to_self');
          }
        }),
        switchMap(() => this.nameValueService.setEndUserNameValue(nameValueKey + this.nameValueExtension, value, this.orgname, this.login)),
        tap(() => {
          if (this.settingsCategory === FWD_SETTINGS_CATEGORY.BUSINESS) {
            this.status.forwardingNumber = value;
          }
        }),
        catchError((error: Error) => {
          this.notificationService.showErrorCustomMessage(error.message);
          return of();
        })
      )
      .subscribe();
  }
}
